import { WorkspaceEvent, workspaceEventSchema } from "../workspaceEvents";
import {
  AgentStreamResponse,
  AgentAction,
  TodoItem,
  Artifact,
  MessageAttachment,
  OpportunityDraftFinding,
  AskUserQuestionPayload,
  SessionStatusEnum,
} from "../types";
import { Citation } from "../../teammate/types";
import { parseAskUserQuestion } from "./askUserQuestion";

export interface ProcessedStreamMessage {
  type: "action" | "citation" | "final_response" | "error" | "complete";
  data: unknown;
  citations?: Citation[];
  todos?: TodoItem[];
  artifacts?: Artifact[];
}

export interface StreamProcessorCallbacks {
  onAction?: (action: AgentAction) => void;
  onCitation?: (citations: Citation[]) => void;
  onTodos?: (todos: TodoItem[]) => void;
  onMarkdownChunk?: (content: string) => void;
  onFinalResponse?: (response: {
    content: string;
    artifacts?: Artifact[];
    heading?: string;
    interaction?: AgentStreamResponse["interaction"];
  }) => void;
  onError?: (error: string) => void;
  onComplete?: () => void;
}

export interface StreamProcessorOptions {
  isMarkdownResponse?: boolean;
  callbacks?: StreamProcessorCallbacks;
}

export interface ProcessedStreamResult {
  content: string;
  actions: AgentAction[];
  citations?: Citation[];
  todos?: TodoItem[];
  artifacts?: Artifact[];
  heading?: string;
  interaction?: AgentStreamResponse["interaction"];
  confidence_str?: string;
  /** AI-7498: the agent-drafted opportunity, replayed from a persisted turn. */
  opportunityDraft?: OpportunityDraftFinding;
  /** The agent's clarifying question, replayed from a persisted turn. */
  askUserQuestion?: AskUserQuestionPayload;
}

/**
 * Processes a stream of AgentStreamResponse messages
 * @param reader - ReadableStreamDefaultReader for the response stream
 * @param options - Configuration options and callbacks
 * @returns Promise with final processed response
 */
export async function processAgentStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options: StreamProcessorOptions = {}
): Promise<ProcessedStreamResult> {
  const { isMarkdownResponse = false, callbacks } = options;
  const decoder = new TextDecoder();

  let actions: AgentAction[] = [];
  let citations: Citation[] | undefined = undefined;
  let todos: TodoItem[] | undefined = undefined;
  let stepHeading = "";
  let isReading = true;
  let markdownBuffer = "";
  let finalContent = "";
  let finalArtifacts: Artifact[] = [];
  let finalInteraction: AgentStreamResponse["interaction"] | undefined;
  let finalConfidenceStr: string | undefined;
  let finalAskUserQuestion: AskUserQuestionPayload | undefined;
  let incompleteLineBuffer = ""; // Buffer for incomplete lines across chunks

  const addOrUpdateAction = (actionsList: AgentAction[], newAction: AgentAction): AgentAction[] => {
    const existingIndex = actionsList.findIndex(action => action.id && action.id === newAction.id);
    if (existingIndex !== -1) {
      // Update existing action
      const updated = [...actionsList];
      updated[existingIndex] = { ...updated[existingIndex], ...newAction };
      return updated;
    } else {
      // Add new action
      return [...actionsList, newAction];
    }
  };

  const handleJSONStr = (jsonStr: string) => {
    try {
      const msg = JSON.parse(jsonStr) as AgentStreamResponse;

      stepHeading = msg.heading || stepHeading;

      if (msg.type === "info" || msg.type === "analysis") {
        const action: AgentAction = {
          type: msg.type,
          id: msg.id,
          content: msg.content || "",
          timestamp: Date.now(),
          artifacts: msg.artifacts,
        };
        actions = addOrUpdateAction(actions, action);
        callbacks?.onAction?.(action);
      } else if (msg.type === "text") {
        // Handle text messages - add as info action
        if (msg.content) {
          const action: AgentAction = {
            type: "info",
            content: msg.content,
            timestamp: Date.now(),
          };
          actions = addOrUpdateAction(actions, action);
          callbacks?.onAction?.(action);
        }
      } else if (msg.type === "tool_use") {
        // Handle tool usage
        if (msg.tool !== "TodoWrite") {
          // For non-TodoWrite tools, add as info action
          const toolContent = msg.tool ? `Using tool: ${msg.tool}` : "Tool usage";
          const action: AgentAction = {
            type: "info",
            id: msg.id,
            content: toolContent,
            timestamp: Date.now(),
          };
          actions = addOrUpdateAction(actions, action);
          callbacks?.onAction?.(action);
        }

        // If todos are present, update the todos list
        if (msg.todos && msg.todos.length > 0) {
          todos = msg.todos;
          callbacks?.onTodos?.(msg.todos);
        }
      } else if (msg.type === "citations") {
        citations = msg.citations;
        callbacks?.onCitation?.(msg.citations || []);
      } else if (msg.type === "ask_user_question") {
        // The agent's clarifying question, shown as a question card.
        finalAskUserQuestion = parseAskUserQuestion(msg.content) ?? finalAskUserQuestion;
      } else if (msg.type === "require_user_action") {
        finalContent = msg.content || "";
        finalArtifacts = msg.artifacts || [];
        finalInteraction = msg.interaction;
      } else if (msg.type === "agent_outcome" || msg.type === "error") {
        citations = msg.citations;
        finalContent = msg.content || "";
        finalArtifacts = msg.artifacts || [];

        // Only capture confidence_str from agent_outcome messages
        if (msg.type === "agent_outcome") {
          finalConfidenceStr = msg.confidence_str;
        }

        if (msg.type === "error") {
          callbacks?.onError?.(msg.content || "Unknown error");
        }
      } else if (msg.type === "final_response") {
        finalContent = msg.response || "";
        finalArtifacts = msg.artifacts || [];

        // Add tool usage summary if present
        if (msg.data?.tool_usage) {
          const toolSummary = `Tools used: ${msg.data.tool_usage.map(t => t.tool).join(", ")}`;
          const summaryAction: AgentAction = {
            type: "info",
            content: toolSummary,
            timestamp: Date.now(),
          };
          actions = addOrUpdateAction(actions, summaryAction);
          callbacks?.onAction?.(summaryAction);
        }
      } else if (msg.type === "complete") {
        // Stream complete
        if (!finalContent) {
          finalContent = "Process completed";
        }
        callbacks?.onComplete?.();
      }
    } catch (e) {
      // Only log actual JSON parsing errors
      if (jsonStr.trim() && jsonStr.startsWith("{")) {
        console.error("Failed to parse message:", jsonStr, e);
        callbacks?.onError?.(`Failed to parse message: ${e}`);
      } else if (jsonStr.trim() && !jsonStr.startsWith("{")) {
        // For non-JSON content (markdown/plain text), accumulate it
        markdownBuffer += jsonStr + "\n";
        finalContent = markdownBuffer;
        callbacks?.onMarkdownChunk?.(markdownBuffer);
      }
      // Ignore empty lines and whitespace-only lines
    }
  };

  while (isReading) {
    const { done, value } = await reader.read();
    if (done) {
      isReading = false;
      // Process any remaining incomplete line buffer before breaking
      if (incompleteLineBuffer.trim() && !isMarkdownResponse) {
        const sseMatch = incompleteLineBuffer.match(/^data:\s*(.+)$/);
        const jsonStr = sseMatch ? sseMatch[1] : incompleteLineBuffer;

        try {
          handleJSONStr(jsonStr);
        } catch (e) {
          if (jsonStr.trim() && !jsonStr.startsWith("{")) {
            markdownBuffer += "\n" + jsonStr;
            finalContent = markdownBuffer;
            callbacks?.onMarkdownChunk?.(markdownBuffer);
          }
        }
      }
      break;
    }

    const chunk = decoder.decode(value);

    if (isMarkdownResponse) {
      // For markdown responses, accumulate the text
      markdownBuffer += chunk;
      finalContent = markdownBuffer;

      // Call the markdown chunk callback for streaming updates
      callbacks?.onMarkdownChunk?.(markdownBuffer);
    } else {
      // Prepend any incomplete line from previous chunk
      const fullChunk = incompleteLineBuffer + chunk;

      // Split by newlines, but keep track of whether the chunk ended with a newline
      const lines = fullChunk.split("\n");

      // If the chunk didn't end with a newline, the last element is incomplete
      // Store it for the next iteration
      if (!chunk.endsWith("\n")) {
        incompleteLineBuffer = lines.pop() || "";
      } else {
        incompleteLineBuffer = "";
      }

      // Process only complete lines
      const completeLines = lines.filter(Boolean);

      for (const line of completeLines) {
        // Handle SSE format: data: {...}
        const sseMatch = line.match(/^data:\s*(.+)$/);
        const jsonStr = sseMatch ? sseMatch[1] : line;

        handleJSONStr(jsonStr);
      }
    }
  }

  const finalResponse: ProcessedStreamResult = {
    content: finalContent || (isMarkdownResponse ? markdownBuffer : ""),
    actions,
    citations,
    todos,
    artifacts: finalArtifacts,
    heading: stepHeading,
    interaction: finalInteraction,
    confidence_str: finalConfidenceStr,
    askUserQuestion: finalAskUserQuestion,
  };

  callbacks?.onFinalResponse?.({
    content: finalResponse.content,
    artifacts: finalResponse.artifacts,
    heading: finalResponse.heading,
    interaction: finalResponse.interaction,
  });

  return finalResponse;
}

/**
 * Converts processed stream response to AgentStreamResponse format
 */
export function streamResponseToAgentMessages(
  streamResponse: ProcessedStreamResult
): AgentStreamResponse[] {
  const messages: AgentStreamResponse[] = [];

  // Add info actions as separate messages
  streamResponse.actions.forEach(action => {
    messages.push({
      type: action.type,
      content: action.content,
      id: action.id,
      artifacts: action.artifacts,
    });
  });

  // Add final agent outcome
  if (streamResponse.content) {
    messages.push({
      type: "agent_outcome",
      content: streamResponse.content,
      citations: streamResponse.citations,
      artifacts: streamResponse.artifacts,
      heading: streamResponse.heading,
      interaction: streamResponse.interaction,
      confidence_str: streamResponse.confidence_str,
    });
  }

  return messages;
}

/**
 * Processes existing conversation data (not from stream) using the same logic as processAgentStream
 * Useful for loading historical conversations or existing chat data
 */
export function processExistingConversation(
  conversationTurns: Array<{
    role: "user" | "assistant";
    content: AgentStreamResponse[];
  }>
): ProcessedStreamResult[] {
  return conversationTurns.map(turn => {
    if (turn.role === "user") {
      // For user messages, just return the content as is
      return {
        content: turn.content.map(msg => msg.content || "").join(" "),
        actions: [],
        citations: undefined,
        todos: undefined,
        artifacts: undefined,
        heading: undefined,
        interaction: undefined,
      };
    } else {
      // For assistant messages, process like stream processor
      return processAgentMessageContent(turn.content);
    }
  });
}

/**
 * Processes a single assistant message's content using the same logic as the stream processor
 * This function is used by both processExistingConversation and can be used standalone
 */
export function processAgentMessageContent(content: AgentStreamResponse[]): ProcessedStreamResult {
  let actions: AgentAction[] = [];
  let citations: Citation[] | undefined = undefined;
  let todos: TodoItem[] | undefined = undefined;
  let finalContent = "";
  let finalArtifacts: Artifact[] = [];
  let finalHeading = "";
  let finalInteraction: AgentStreamResponse["interaction"] | undefined;
  let finalConfidenceStr: string | undefined;
  let finalOpportunityDraft: OpportunityDraftFinding | undefined;
  let finalAskUserQuestion: AskUserQuestionPayload | undefined;

  content.forEach(msg => {
    if (msg.type === "info" || msg.type === "analysis") {
      actions.push({
        type: msg.type,
        id: msg.id,
        content: msg.content || "",
        timestamp: Date.now(),
        artifacts: msg.artifacts,
      });
    } else if (msg.type === "text") {
      // Handle text messages as info actions
      if (msg.content) {
        actions.push({
          type: "info",
          content: msg.content,
          timestamp: Date.now(),
        });
      }
    } else if (msg.type === "tool_use") {
      if (msg.tool !== "TodoWrite") {
        const toolContent = msg.tool ? `Using tool: ${msg.tool}` : "Tool usage";
        actions.push({
          type: "info",
          id: msg.id,
          content: toolContent,
          timestamp: Date.now(),
        });
      }
      if (msg.todos && msg.todos.length > 0) {
        todos = msg.todos;
      }
    } else if (msg.type === "citations") {
      citations = msg.citations;
    } else if (msg.type === "opportunity_draft") {
      // AI-7498: replay the drafted opportunity so re-fetching a session keeps
      // the editable form on the turn that produced it.
      if (msg.finding) {
        finalOpportunityDraft = msg.finding as unknown as OpportunityDraftFinding;
      }
    } else if (msg.type === "ask_user_question") {
      // Replay the question card on the turn that asked it.
      finalAskUserQuestion = parseAskUserQuestion(msg.content) ?? finalAskUserQuestion;
    } else if (msg.type === "require_user_action") {
      finalContent = msg.content || "";
      finalArtifacts = msg.artifacts || [];
      finalInteraction = msg.interaction;
      finalHeading = msg.heading || "";
    } else if (msg.type === "agent_outcome" || msg.type === "error") {
      citations = msg.citations;
      finalContent = msg.content || "";
      finalArtifacts = msg.artifacts || [];
      finalHeading = msg.heading || "";

      // Only capture confidence_str from agent_outcome messages
      if (msg.type === "agent_outcome") {
        finalConfidenceStr = msg.confidence_str;
      }
    } else if (msg.type === "final_response") {
      finalContent = msg.response || "";
      finalArtifacts = msg.artifacts || [];
      finalHeading = msg.heading || "";

      // Add tool usage summary if present
      if (msg.data?.tool_usage) {
        const toolSummary = `Tools used: ${msg.data.tool_usage.map(t => t.tool).join(", ")}`;
        actions.push({
          type: "info",
          content: toolSummary,
          timestamp: Date.now(),
        });
      }
    } else if (msg.type === "complete") {
      if (!finalContent) {
        finalContent = "Process completed";
      }
    }
  });

  return {
    content: finalContent,
    actions,
    citations,
    todos,
    artifacts: finalArtifacts,
    heading: finalHeading,
    interaction: finalInteraction,
    confidence_str: finalConfidenceStr,
    opportunityDraft: finalOpportunityDraft,
    askUserQuestion: finalAskUserQuestion,
  };
}

/**
 * Processes playground session turns into chat sessions format
 * This is specifically for playground usage where we need to convert session turns
 * into a format that can be consumed by the ChatProvider
 */
/** A saved turn's workspace event, whether or not its content went through the
 * response schema (session reads can hand over the raw saved items). */
const savedWorkspaceEvent = (event: unknown): WorkspaceEvent[] => {
  const normalized = (event as { workspaceEvent?: WorkspaceEvent } | null)?.workspaceEvent;
  if (normalized) return [normalized];
  const parsed = workspaceEventSchema.safeParse(event);
  return parsed.success ? [parsed.data] : [];
};

export function processPlaygroundSessionTurns(
  turns: Array<{
    role: "user" | "assistant";
    content: AgentStreamResponse[];
    id?: string;
    timestamp?: number;
    attachments?: MessageAttachment[];
  }>,
  sessionId: string,
  datamates: string[],
  context: Record<string, unknown> = {},
  status: SessionStatusEnum
): Record<
  string,
  {
    id: string;
    messages: Array<{
      id: string;
      role: "user" | "assistant";
      content: string;
      timestamp: number;
      heading: string;
      actions?: AgentAction[];
      artifacts?: Artifact[];
      citations?: Citation[];
      todos?: TodoItem[];
      interaction?: AgentStreamResponse["interaction"];
      attachments?: MessageAttachment[];
      opportunityDraft?: OpportunityDraftFinding;
      askUserQuestion?: AskUserQuestionPayload;
      workspaceEvents?: WorkspaceEvent[];
    }>;
  }
> {
  const session: Record<
    string,
    {
      id: string;
      datamates: string[];
      context: Record<string, unknown>;
      status: SessionStatusEnum;
      messages: Array<{
        id: string;
        role: "user" | "assistant";
        content: string;
        timestamp: number;
        heading: string;
        actions?: AgentAction[];
        artifacts?: Artifact[];
        citations?: Citation[];
        todos?: TodoItem[];
        interaction?: AgentStreamResponse["interaction"];
        attachments?: MessageAttachment[];
        opportunityDraft?: OpportunityDraftFinding;
        askUserQuestion?: AskUserQuestionPayload;
        workspaceEvents?: WorkspaceEvent[];
      }>;
    }
  > = {
    [sessionId.toString()]: {
      id: sessionId,
      messages: [],
      datamates,
      context,
      status,
    },
  };

  turns.forEach((turn, index) => {
    const messageId = turn.id || `${turn.role}-${index}`;
    const timestamp = turn.timestamp || Date.now() - (turns.length - index) * 1000;

    if (turn.role === "user") {
      // For user messages, extract text content
      const content = turn.content
        .filter(msg => msg.type === "text" || msg.content)
        .map(msg => msg.content || "")
        .join(" ");

      session[sessionId].messages.push({
        id: messageId,
        role: "user",
        content,
        timestamp,
        heading: "User",
        attachments: turn.attachments || [],
      });
    } else {
      // For assistant messages, process using our common function
      const processed = processAgentMessageContent(turn.content);

      session[sessionId].messages.push({
        id: messageId,
        role: "assistant",
        content: processed.content,
        timestamp,
        heading: processed.heading || "",
        actions: processed.actions,
        artifacts: processed.artifacts,
        citations: processed.citations,
        todos: processed.todos,
        interaction: processed.interaction,
        attachments: turn.attachments || [],
        opportunityDraft: processed.opportunityDraft,
        askUserQuestion: processed.askUserQuestion,
        workspaceEvents: turn.content.flatMap(savedWorkspaceEvent),
      });
    }
  });

  return session;
}

/**
 * Helper function to create a fetch request and process the stream
 */
export async function sendMessageAndProcessStream(
  url: string,
  data: Record<string, unknown>,
  options: {
    headers?: Record<string, string>;
    signal?: AbortSignal;
    isMarkdownResponse?: boolean;
    callbacks?: StreamProcessorCallbacks;
  } = {}
): Promise<ProcessedStreamResult> {
  const { headers = {}, signal, isMarkdownResponse, callbacks } = options;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(data),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response stream available");
  }

  return processAgentStream(reader, { isMarkdownResponse, callbacks });
}
